import {
  BaseProcess,
  BaseProcessMetadata,
  Connection as GraphConnection,
  GenericGraphProcess,
  GenericGraphProcesses,
  CommonGraph
} from '@common/types';
import type { PropertiesDidChange, PropertiesDidSave } from '@LSP/base/hook';
import { Dialog, Select, Stack, Text, theme, Tooltip } from '@prophecy/ui';
import { useFitView } from '@prophecy/ui/Graph/hooks';
import { LayoutAlgo, NodeData, NodeType, Point, StrippedNodeData } from '@prophecy/ui/Graph/types';
import { snapPosition } from '@prophecy/ui/Graph/utils';
import { produceWithoutFreeze } from '@prophecy/utils/data';
import { afterAnimationFrame } from '@prophecy/utils/dom';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
/**
 * Note: clone with JSON.parse(JSON.stringify(obj)) looses constructor information.
 * Due to which isEqual check between two obj can break. So we clone graph using lodash deepClone
 * which maintains constructor information added by immer.
 */
import { isEqual } from 'lodash-es';
import React from 'react';
import { Node, useReactFlow } from 'reactflow';

import { useHistory } from '../../../HistoryManager/context';
import { HistoryType } from '../../../HistoryManager/types';
import { frameId } from '../../../HistoryManager/utils';
import { getProcessesPath, isMetaInputGemType, isMetaOutputGemType } from '../../../Parser/bindings';
import { SELECTED_LAYOUT_ALGO_KEY } from '../../../utils/localstorage-keys';
import { isNotSubGraphNode } from '../subgraph/subgraph';
import { OnNodeDragStopHandler } from './types';

export const filterAutoPositionedNodes = (node: Node<StrippedNodeData>) => {
  return isNotSubGraphNode(node) && !isMetaInputGemType(node.data.gemType) && !isMetaOutputGemType(node.data.gemType);
};

export function useOnSelectionDragStop(
  root: CommonGraph,
  graphPath: string,
  propertiesDidChange: PropertiesDidChange,
  propertiesDidSave: PropertiesDidSave
) {
  return usePersistentCallback(function onSelectionDragStop(event: React.MouseEvent, nodes: Node[]) {
    propertiesDidChange(
      nodes.map((node) => ({
        property: `${getProcessesPath(graphPath)}.${node.id}.metadata`,
        value: {
          ...(root.processes[node.id]?.metadata || {}),
          ...snapPosition(node.position)
        }
      }))
    );

    propertiesDidSave();
  });
}

export function useOnNodeDragStop(
  root: CommonGraph,
  graphPath: string,
  propertiesDidChange: PropertiesDidChange,
  propertiesDidSave: PropertiesDidSave
): OnNodeDragStopHandler {
  const instance = useReactFlow();
  return usePersistentCallback(function onNodeDragStop(historyMergeId?: string) {
    const internalNodes: Node[] = instance.getNodes();

    // on drag stop snap all nodes
    const updatedNodes = internalNodes.map((node) => {
      if (isNotSubGraphNode(node)) {
        return { ...node, position: snapPosition(node.position), dragging: false };
      }
      return node;
    });

    // update the snapped nodes only if positions are changed
    if (
      isEqual(
        updatedNodes.map((node) => node.position),
        internalNodes.map((node) => node.position)
      )
    ) {
      // exit early if positions are not changed
      return;
    } else {
      instance.setNodes(updatedNodes);
    }

    // Note: react-flow only invoke drag event for last selected node in case of multiple-selection
    const nodesMoved: { id: string; x: number; y: number }[] = [];
    updatedNodes.filter(isNotSubGraphNode).forEach((node) => {
      if (!root.processes[node.id]) return;
      const { x, y } = snapPosition(root.processes[node.id].metadata);
      const { x: newX, y: newY } = node.position;

      // if the snapped position is not updated no need to call didChange
      if (newX !== x || newY !== y) {
        nodesMoved.push({ id: node.id, ...node.position });
      }
    });

    if (!nodesMoved.length) return;

    propertiesDidChange(
      nodesMoved.map((node) => {
        return {
          property: `${getProcessesPath(graphPath)}.${node.id}.metadata`,
          value: {
            ...root.processes[node.id].metadata,
            x: node.x,
            y: node.y
          }
        };
      }),
      { historyMergeId }
    );

    propertiesDidSave({ historyMergeId });
    return true;
  });
}
export const snapProcessToGrid = (
  process: GenericGraphProcess<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, GraphConnection>
) => {
  const { x, y } = process.metadata;
  return {
    ...process,
    metadata: {
      ...process.metadata,
      ...snapPosition({ x, y })
    }
  };
};

export function getPointsFromProcesses(
  processes: GenericGraphProcess<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, GraphConnection>[]
): Point[] {
  return processes.map((p) => ({ x: p.metadata.x, y: p.metadata.y }));
}

function getCurrentIDELayoutAlgo(supportedLayoutAlgo: LayoutAlgo[]) {
  const savedAlgo = localStorage.getItem(SELECTED_LAYOUT_ALGO_KEY) as LayoutAlgo | null;

  return savedAlgo && supportedLayoutAlgo.includes(savedAlgo) ? savedAlgo : supportedLayoutAlgo[0];
}

export function useOnLayout(
  root: CommonGraph,
  onSuccess: (
    processes: GenericGraphProcesses<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, GraphConnection>,
    nodes: Node<NodeData>[],
    skipHistory?: boolean
  ) => void,
  prepareProcess: (
    process: BaseProcess<BaseProcessMetadata>,
    node: Node<NodeData>
  ) => BaseProcess<BaseProcessMetadata> = (process) => process,
  supportedLayoutAlgo: LayoutAlgo[] = [LayoutAlgo.elk, LayoutAlgo.dagre],
  filterNodes: (node: Node) => boolean = filterAutoPositionedNodes
) {
  const fitView = useFitView(filterNodes, false);
  const history = useHistory();
  const instance = useReactFlow();
  return usePersistentCallback(async function (
    nodes: Node<NodeData>[] | ((layoutAlgo?: LayoutAlgo) => Promise<Node<NodeData>[]>),
    {
      showPrompt = true,
      skipHistory,
      fitToView
    }: { showPrompt?: boolean; skipHistory?: boolean; fitToView?: boolean } = {}
  ) {
    const hasCommentOrSubFlow = instance
      .getNodes()
      .some((node) => node.type === NodeType.subFlow || node.type === NodeType.comment);
    const algo = hasCommentOrSubFlow ? LayoutAlgo.elk : getCurrentIDELayoutAlgo(supportedLayoutAlgo);
    if (showPrompt) {
      const options = hasCommentOrSubFlow
        ? supportedLayoutAlgo.map((algo) => {
            if (algo === LayoutAlgo.dagre) {
              return {
                value: algo,
                label: algo,
                disabled: true,
                tooltip: 'DAG layout is not supported for comment or visual group nodes'
              };
            }
            return { value: algo, label: algo, disabled: false, tooltip: undefined };
          })
        : supportedLayoutAlgo.map((algo) => ({ value: algo, label: algo, disabled: false, tooltip: undefined }));
      const defaultValue = algo;
      const doLayoutDialogResult = await Dialog.alert({
        title: `Auto Layout`,
        children: (
          <Stack gap={theme.spaces.x24} padding={theme.outlineWidth}>
            <Text level='sm'>Auto layout will reset the position of all process. Do you want to continue? </Text>
            {supportedLayoutAlgo.length > 1 ? (
              <Select
                label='Choose Layout Algorithm'
                defaultValue={defaultValue}
                data-test-id='auto-layout-algorithm-select'
                onChange={(value) => {
                  localStorage.setItem(SELECTED_LAYOUT_ALGO_KEY, value);
                }}>
                {options.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    disabled={option.disabled}>
                    <Tooltip title={option.tooltip}>
                      <Stack direction='horizontal' alignY='center'>
                        {option.label}
                      </Stack>
                    </Tooltip>
                  </Select.Option>
                ))}
              </Select>
            ) : null}
          </Stack>
        ),
        'data-test-id': 'auto-layout-dialog'
      });

      if (doLayoutDialogResult !== Dialog.ResponseAction.okButton) return;
    }
    const _nodes = typeof nodes === 'function' ? await nodes(algo) : nodes;

    const processes = produceWithoutFreeze(root?.processes, (draft) => {
      _nodes.forEach((node) => {
        const process = draft[node.id];
        if (!process) return;

        process.metadata.x = node.position.x;
        process.metadata.y = node.position.y;
        // update process width and height if node is subflow
        if (node.type === NodeType.subFlow) {
          process.metadata.width = node.width as number;
          process.metadata.height = node.height as number;
        }

        prepareProcess(process, node);
      });
      return draft;
    });

    const fitViewAfterLayout = () => {
      afterAnimationFrame(() => {
        fitView();
      });
    };

    onSuccess(processes, _nodes, skipHistory);

    if (fitToView) {
      fitViewAfterLayout();
    }

    // add fit logic in history as well, so on undo redo, its center aligned
    if (fitToView && !skipHistory) {
      history?.mergeOrTrackChange(
        {
          prevValue: fitViewAfterLayout,
          nextValue: fitViewAfterLayout,
          metadata: { type: HistoryType.callback }
        },
        frameId()
      );
    }
  });
}
